import { ArrowLeft, Building2, Home, Layers } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import PrimaryButton from '../../ui/PrimaryButton';
import BlockConfigCard from './BlockConfigCard';
import OnboardingStepper from './OnboardingStepper';
import {
  SITE_TYPES,
  VILLA_GROUPINGS,
  createOnboardingData,
  defaultApartmentBlock,
  defaultVillaStreet,
  mixedStructureSummaryLabel,
  structureNameForIndex,
  structureSummaryLabel,
  totalApartmentUnits,
  totalVillaUnits,
  villaGroupingLabel,
} from './communitySiteUtils';

const SITE_TYPE_OPTIONS = [
  { value: SITE_TYPES.APARTMENT, label: 'Apartment', icon: Building2 },
  { value: SITE_TYPES.VILLA, label: 'Villa', icon: Home },
  { value: SITE_TYPES.MIXED, label: 'Mixed', icon: Layers },
];

const NumberStepper = ({ label, value, min, max, onChange }) => (
  <div className="community-number-stepper">
    <span className="community-field-label">{label}</span>
    <div className="community-stepper-controls">
      <button
        type="button"
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
      >
        −
      </button>
      <span>{value}</span>
      <button
        type="button"
        disabled={value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
      >
        +
      </button>
    </div>
  </div>
);

const CommunityStructure = ({ siteId, siteName, onBack, onContinue, isSidebar = true }) => {
  const [siteType, setSiteType] = useState(SITE_TYPES.APARTMENT);
  const [villaGrouping, setVillaGrouping] = useState(VILLA_GROUPINGS.STREET);
  const [blockCount, setBlockCount] = useState(1);
  const [streetCount, setStreetCount] = useState(1);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [apartmentBlocks, setApartmentBlocks] = useState([defaultApartmentBlock(0)]);
  const [villaStreets, setVillaStreets] = useState([
    defaultVillaStreet(0, VILLA_GROUPINGS.STREET),
  ]);

  const syncApartmentBlocks = count => {
    const next = Math.max(siteType === SITE_TYPES.MIXED ? 0 : 1, Math.min(50, count));
    setBlockCount(next);
    setApartmentBlocks(prev => {
      const blocks = [...prev];
      while (blocks.length < next) blocks.push(defaultApartmentBlock(blocks.length));
      while (blocks.length > next) blocks.pop();
      return blocks;
    });
  };

  const syncVillaStreets = count => {
    const next = Math.max(siteType === SITE_TYPES.MIXED ? 0 : 1, Math.min(50, count));
    setStreetCount(next);
    setVillaStreets(prev => {
      const streets = [...prev];
      while (streets.length < next) {
        streets.push(defaultVillaStreet(streets.length, villaGrouping));
      }
      while (streets.length > next) streets.pop();
      return streets;
    });
  };

  const handleSiteTypeChange = nextType => {
    setSiteType(nextType);
    if (nextType === SITE_TYPES.APARTMENT) {
      syncApartmentBlocks(Math.max(1, blockCount || 1));
      setStreetCount(0);
      setVillaStreets([]);
    } else if (nextType === SITE_TYPES.VILLA) {
      syncVillaStreets(Math.max(1, streetCount || 1));
      setBlockCount(0);
      setApartmentBlocks([]);
    } else {
      syncApartmentBlocks(Math.max(1, blockCount || 1));
      syncVillaStreets(Math.max(1, streetCount || 1));
    }
  };

  const handleVillaGroupingChange = grouping => {
    setVillaGrouping(grouping);
    setVillaStreets(prev =>
      prev.map((street, i) => ({
        ...street,
        villaGrouping: grouping,
        name: structureNameForIndex(i, SITE_TYPES.VILLA, grouping),
      }))
    );
  };

  const allStructures = useMemo(() => {
    if (siteType === SITE_TYPES.APARTMENT) return apartmentBlocks;
    if (siteType === SITE_TYPES.VILLA) return villaStreets;
    return [...apartmentBlocks, ...villaStreets];
  }, [siteType, apartmentBlocks, villaStreets]);

  const apartmentUnits = totalApartmentUnits(apartmentBlocks);
  const villaUnits = totalVillaUnits(villaStreets);
  const totalUnits = apartmentUnits + villaUnits;

  const summaryText = useMemo(() => {
    if (siteType === SITE_TYPES.MIXED) {
      return mixedStructureSummaryLabel({
        blockCount,
        streetCount,
        apartmentUnits,
        villaUnits,
      });
    }
    const count = siteType === SITE_TYPES.VILLA ? streetCount : blockCount;
    return structureSummaryLabel(siteType, count, totalUnits);
  }, [siteType, blockCount, streetCount, apartmentUnits, villaUnits, totalUnits]);

  const handleThumbnailChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setThumbnailPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const applyFirstApartmentToAll = () => {
    if (apartmentBlocks.length < 2) return;
    const template = apartmentBlocks[0];
    setApartmentBlocks(
      apartmentBlocks.map((_, i) => ({
        ...template,
        name: structureNameForIndex(i, SITE_TYPES.APARTMENT),
        structureKind: template.structureKind,
      }))
    );
  };

  const applyFirstVillaToAll = () => {
    if (villaStreets.length < 2) return;
    const template = villaStreets[0];
    setVillaStreets(
      villaStreets.map((_, i) => ({
        ...template,
        name: structureNameForIndex(i, SITE_TYPES.VILLA, villaGrouping),
        villaGrouping,
      }))
    );
  };

  const handleContinue = () => {
    if (allStructures.length === 0) {
      alert('Add at least one block or street.');
      return;
    }

    const numberOfBlocks =
      siteType === SITE_TYPES.VILLA
        ? streetCount
        : siteType === SITE_TYPES.MIXED
          ? blockCount + streetCount
          : blockCount;

    const data = createOnboardingData({
      siteId,
      siteType,
      numberOfBlocks,
      thumbnailPath: thumbnailPreview,
      blocks: allStructures,
    });

    onContinue(data);
  };

  const SummaryIcon =
    siteType === SITE_TYPES.VILLA ? Home : siteType === SITE_TYPES.MIXED ? Layers : Building2;

  return (
    <div className={`community-onboarding-container ${isSidebar ? 'sidebar-mode' : ''}`}>
      <div className="community-onboarding-header">
        <button type="button" onClick={onBack} className="back-button" title="Back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="community-onboarding-header-text">
          <h2 className="community-onboarding-title">Property Structure</h2>
          {siteName && <p className="community-onboarding-subtitle">{siteName}</p>}
        </div>
      </div>

      <OnboardingStepper currentStep={0} />

      <div className="community-onboarding-scroll">
        <div className="community-summary-header">
          <SummaryIcon className="w-8 h-8 text-[#0a8089] shrink-0" />
          <div>
            <p className="community-summary-title">{siteName}</p>
            <p className="community-summary-text">{summaryText}</p>
          </div>
        </div>

        <section className="community-section">
          <h3 className="community-section-title">Site Details</h3>
          <p className="community-section-subtitle">Basic property information</p>

          <span className="community-field-label">Site Type</span>
          <div className="community-segmented">
            {SITE_TYPE_OPTIONS.map(opt => {
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={siteType === opt.value ? 'active' : ''}
                  onClick={() => handleSiteTypeChange(opt.value)}
                >
                  <Icon className="w-4 h-4" />
                  {opt.label}
                </button>
              );
            })}
          </div>

          {(siteType === SITE_TYPES.VILLA || siteType === SITE_TYPES.MIXED) && (
            <label className="community-field">
              <span>Group Villas by</span>
              <select
                className="community-grouping-select"
                value={villaGrouping}
                onChange={e => handleVillaGroupingChange(e.target.value)}
              >
                {Object.values(VILLA_GROUPINGS).map(g => (
                  <option key={g} value={g}>
                    {villaGroupingLabel(g)}
                  </option>
                ))}
              </select>
            </label>
          )}

          {(siteType === SITE_TYPES.APARTMENT || siteType === SITE_TYPES.MIXED) && (
            <NumberStepper
              label="Number of Blocks"
              value={blockCount}
              min={siteType === SITE_TYPES.MIXED ? 0 : 1}
              max={50}
              onChange={syncApartmentBlocks}
            />
          )}

          {(siteType === SITE_TYPES.VILLA || siteType === SITE_TYPES.MIXED) && (
            <NumberStepper
              label={`Number of ${villaGroupingLabel(villaGrouping)}s`}
              value={streetCount}
              min={siteType === SITE_TYPES.MIXED ? 0 : 1}
              max={50}
              onChange={syncVillaStreets}
            />
          )}

          <div className="community-thumbnail-picker">
            <span className="community-field-label">Site Thumbnail (optional)</span>
            <label className="community-thumbnail-upload">
              {thumbnailPreview ? (
                <img src={thumbnailPreview} alt="Site thumbnail" />
              ) : (
                <span>Upload site image</span>
              )}
              <input type="file" accept="image/*" onChange={handleThumbnailChange} hidden />
            </label>
          </div>
        </section>

        {(siteType === SITE_TYPES.APARTMENT || siteType === SITE_TYPES.MIXED) &&
          apartmentBlocks.length > 0 && (
            <section className="community-section">
              <h3 className="community-section-title">
                {siteType === SITE_TYPES.MIXED ? 'Apartment Blocks' : 'Block Configuration'}
              </h3>
              <p className="community-section-subtitle">Block → Floor → Flat</p>
              {apartmentBlocks.length > 1 && (
                <button
                  type="button"
                  className="community-outline-btn"
                  onClick={applyFirstApartmentToAll}
                >
                  Apply Block A to All
                </button>
              )}
              {apartmentBlocks.map((block, i) => (
                <BlockConfigCard
                  key={`apt-${i}-${block.name}`}
                  block={block}
                  blockIndex={i}
                  onChange={updated => {
                    const next = [...apartmentBlocks];
                    next[i] = updated;
                    setApartmentBlocks(next);
                  }}
                  onClone={() => {
                    const next = [...apartmentBlocks];
                    next.splice(i + 1, 0, {
                      ...block,
                      name: `${block.name} (Copy)`,
                    });
                    setBlockCount(next.length);
                    setApartmentBlocks(next);
                  }}
                />
              ))}
            </section>
          )}

        {(siteType === SITE_TYPES.VILLA || siteType === SITE_TYPES.MIXED) &&
          villaStreets.length > 0 && (
            <section className="community-section">
              <h3 className="community-section-title">
                {siteType === SITE_TYPES.MIXED ? 'Villa Streets' : 'Property Structure'}
              </h3>
              <p className="community-section-subtitle">Street → Villa</p>
              {villaStreets.length > 1 && (
                <button
                  type="button"
                  className="community-outline-btn"
                  onClick={applyFirstVillaToAll}
                >
                  Apply {villaGroupingLabel(villaGrouping)} A to All
                </button>
              )}
              {villaStreets.map((street, i) => (
                <BlockConfigCard
                  key={`villa-${i}-${street.name}`}
                  block={street}
                  blockIndex={i}
                  onChange={updated => {
                    const next = [...villaStreets];
                    next[i] = updated;
                    setVillaStreets(next);
                  }}
                  onClone={() => {
                    const next = [...villaStreets];
                    next.splice(i + 1, 0, {
                      ...street,
                      name: `${street.name} (Copy)`,
                    });
                    setStreetCount(next.length);
                    setVillaStreets(next);
                  }}
                />
              ))}
            </section>
          )}
      </div>

      <div className="community-onboarding-footer">
        <PrimaryButton className="w-full h-12 text-base font-semibold" onClick={handleContinue}>
          Continue to Amenities
        </PrimaryButton>
      </div>
    </div>
  );
};

export default CommunityStructure;
